/**
 * Registry of routing canaries — decisions about which backend serves a route, made in the
 * middleware before render. Unlike {@link AMPLITUDE_EXPERIMENTS}, which toggle UI on the client.
 *
 * Adding a canary is one {@link defineCanary} entry in {@link CANARIES}; the header, cookie,
 * middleware guard and exposure event are all derived from it.
 *
 * No SDK imports here, so the middleware, SSR readers and client components can all share it.
 */

import { isNativeApiAllowedRoute } from '@/logic/app-data/app-data-route';

export type Canary<TVariant extends string = string> = {
    /** Stable slug behind the `x-canary-<id>` header and `canary_<id>` cookie. */
    id: string;
    /** Amplitude flag key, declared in `@/utils/experiments`. */
    flagKey: string;
    /** MUST be the existing, proven path: every failure mode resolves here. */
    control: TVariant;
    /** Variants the routing code can serve. Anything else degrades to `control`. */
    variants: readonly TVariant[];
    /** Paths this canary decides. Keep narrow — it gates an Amplitude evaluation. */
    matches: (path: string) => boolean;
};

/** Preserves the literal union of `variants` so consumers get a typed variant, not `string`. */
export const defineCanary = <const TVariant extends string>(canary: Canary<TVariant>): Canary<TVariant> => canary;

export type ContentApiVariant = 'page-config' | 'gfe' | 'native-api';

export const NATIVE_API_FLAG_KEY = 'casino-next-content-api';

export const NATIVE_API_CANARY = defineCanary<ContentApiVariant>({
    id: 'native-api',
    flagKey: NATIVE_API_FLAG_KEY,
    control: 'page-config',
    variants: ['page-config', 'gfe', 'native-api'],
    matches: isNativeApiAllowedRoute
});

/**
 * Canaries cover logged-in visitors only — logged-out responses are CDN-cached, so one person's
 * assignment would be served to everyone behind the same cache entry. Size rollouts accordingly.
 */
export const CANARIES: readonly Canary[] = [NATIVE_API_CANARY];

export const canaryHeaderName = (canary: Canary): string => `x-canary-${canary.id}`;

export const canaryCookieName = (canary: Canary): string => `canary_${canary.id}`;

export const getCanariesForPath = (path: string): Canary[] => CANARIES.filter((canary) => canary.matches(path));

/** Narrows an untrusted string (header, cookie) to a variant this canary declares. */
export const toVariant = <TVariant extends string>(canary: Canary<TVariant>, value: string | null | undefined) =>
    canary.variants.find((variant) => variant === value);
